/**
 * A escolha do PORTAL — 0, 1 ou N portais (lote P2 · spec 11.3 · D-131).
 *
 * Os três casos são diferentes e nenhum deles é "erro":
 *
 *   **N** — a pessoa escolhe. É a tela abaixo.
 *   **1** — não há escolha; entrar direto é o certo, e o menu do header não mostra a
 *           troca de portal (trocar para o quê?).
 *   **0** — usuário ATIVO sem portal ativo: o acesso existe e não inclui empresa
 *           nenhuma (R-05). Não é falha de login, e tratá-lo como erro genérico faria
 *           a pessoa tentar a senha de novo, indefinidamente, por um problema que não
 *           é dela. A tela diz o que é e oferece a única saída: sair.
 *
 * Cada cartão diz o nome do portal, a visão e quantas empresas ele reúne. As empresas em si
 * aparecem DENTRO do portal, no seletor da página: aqui a decisão é só por onde entrar.
 *
 * ## A mesma moldura do login (PK-09)
 *
 * No protótipo (11.3:65-71) a escolha é o passo 2 DENTRO do cartão do login: a mesma logo, o
 * mesmo selo e o mesmo cartão, com a lista no lugar do formulário. Era uma tela à parte, com a
 * marca escrita à mão, e parecia outro produto no meio da entrada.
 */
import { ArrowLeft, ArrowRight, LogOut } from 'lucide-react'
import type { EuExternoDoPortal, PortalDoExterno } from '@dv/contracts'
import { nomeDaVisao, quantasEmpresas } from '../formato.js'

function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((parte) => parte.length > 0)
  const primeira = partes[0]?.[0] ?? '?'
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : ''
  return `${primeira}${ultima}`.toUpperCase()
}

export function EscolhaDoPortal({
  eu,
  escolher,
  sair,
}: {
  eu: EuExternoDoPortal
  escolher: (portal: PortalDoExterno) => void
  sair: () => void
}) {
  return (
    <main className="entrada">
      <div className="entrada-conteudo">
        <img className="marca-login" src="/marca/logo-digitalview-navy.png" alt="DigitalView" width={168} height={33} />
        <p className="portal-tipo eyebrow">PORTAL DE CLIENTES E PARCEIROS</p>
        <section className="login-card chooser">
          {eu.portais.length === 0 ? (
            <>
              <p className="eyebrow">BEM-VINDO, {eu.nome.toUpperCase()}</p>
              <h1>Seu acesso não inclui nenhuma empresa</h1>
              <p className="intro">
                Sua conta existe, mas não está ligada a nenhuma empresa no momento. Fale com seu
                contato na DigitalView.
              </p>
              <button className="primario" type="button" onClick={sair} data-teste="sair-sem-portal">
                <LogOut size={16} /> Sair
              </button>
            </>
          ) : (
            <>
              <p className="eyebrow">ACESSO EXTERNO</p>
              <h1>Escolha o portal</h1>
              {/* A saudação, sem "Escolha em qual portal você quer entrar": o título já diz (D-203). */}
              <p className="intro">
                Olá, <strong>{eu.nome}</strong>.
              </p>
              <div className="vinculos" data-teste="lista-de-portais">
                {eu.portais.map((portal) => (
                  <button
                    key={portal.id}
                    type="button"
                    onClick={() => {
                      escolher(portal)
                    }}
                  >
                    <span className="avatar-vinculo" aria-hidden="true">
                      {iniciaisDe(portal.nome)}
                    </span>
                    <span>
                      <strong>{portal.nome}</strong>
                      <small>
                        {nomeDaVisao(portal.visao)} · {quantasEmpresas(portal.empresas.length)}
                      </small>
                    </span>
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                ))}
              </div>
              <button className="link sair-do-chooser" type="button" onClick={sair}>
                <ArrowLeft size={14} aria-hidden="true" /> Entrar com outra conta
              </button>
            </>
          )}
        </section>
      </div>
    </main>
  )
}
